/**
 * Resolved steps — the **record** half of the draw-and-record architecture. A
 * resolution (an attack, a tech action, a save) runs **once on the authority**
 * through `resolve` (in `@lancer-vtt/rules`), which draws its dice and
 * **records each draw and outcome as a flat, self-describing step** into an ordered
 * **resolved step list**. That list *is* the command / log entry / broadcast payload.
 *
 * The two-layer model:
 *   - **Effect-atoms** (the *recipe*: unrolled dice, untaken branches) live in the
 *     resolver and the overlay — they are NOT here.
 *   - **Resolved steps** (the *record*: dice already rolled, branch already taken)
 *     live here. They are plain JSON (the same no-class/no-`Map` invariant as the
 *     rest of the encounter domain) so the whole list travels the wire, lands in the
 *     retained log, and persists as a blob.
 *
 * **Why steps live in `@lancer-vtt/domain`, not `@lancer-vtt/rules`:** playback
 * (`applyResolved`) is **rules-free** — it just folds recorded deltas onto state —
 * and the reducer/history (both in `domain`) run it. The *rules* (computing hit vs
 * evasion, rolling) live one layer up in `rules`, which produces these steps. This
 * keeps the one-way `domain ← rules` dependency intact: `rules` imports these step
 * types to emit them; `domain` never imports `rules`.
 *
 * **Inverses live at the step layer.** Each *grounded* step that mutates state
 * (`hp_delta`) self-inverts (`hp_delta -5` ⟷ `+5`); record-only steps (`to_hit`,
 * `damage`) carry no delta and invert to nothing. So undo is a closed problem over
 * this small set, not something every authored effect must get right.
 *
 * The union stays open — a new resolved consequence joins it as a sibling step.
 */

import type { AttackFx, SaveFx } from "./attackFx";
import type { AreaEffect, Cell, Combatant, CombatantId, DamageType, DisclosureLevel, EventEnd, RulingSpec, StatusExpiry, StatusInstance } from "./types";

// ── the disclosure sentinels — the ONE non-id value a recorded source name ever holds ────────

/**
 * The replacement name for a recorded source that named a **weapon** a class-disclosure NPC's
 * dossier locks, and {@link UNDISCLOSED_FEATURE_NAME} its sibling for everything else the class
 * tier hides (systems, traits, reactions, tech).
 *
 * **Written by exactly one caller**: {@link import("./resolutionRedaction").redactUndisclosedSteps},
 * run once per resolution by `EncounterRoom.broadcastResolution`
 * (`backend/src/encounterRoomCore.ts`) before either broadcast sink. No resolver ever emits one —
 * a source arriving at the room is always the true id/name, and the room decides what a table may
 * read. See `decisions/0081` for the disclosure rule and
 * `projects/accuracy-contribution-weapon-redaction.md` for why the degrade REPLACES the name
 * rather than dropping the field.
 *
 * **Both strings deliberately contain a space**, so both fail the narrator's raw-id grammar
 * (`^[a-z0-9_]+(:\d+)?$`, `displaySourceName` in `packages/rules/src/encounter/narrate/narrate.ts`)
 * and pass through its display-name guard verbatim. The receipt therefore reads `undisclosed
 * weapon −1` / `DAMAGE   undisclosed weapon   5 explosive  = 5` with no narrator, card or CLI
 * change at all. Anything that changes these literals must keep that property.
 */
export const UNDISCLOSED_WEAPON_NAME = "undisclosed weapon";

/** The feature-side sentinel — see {@link UNDISCLOSED_WEAPON_NAME} for the whole contract. */
export const UNDISCLOSED_FEATURE_NAME = "undisclosed feature";

/**
 * `ResolutionAbout.actor` for a resolution the GM authored with no acting combatant (`gm_damage`).
 * The GM's own slot id, so it cannot collide with a combatant id minted from a unit name.
 */
export const GM_ACTOR = "gm";

// ── individual recorded steps ────────────────────────────────────────────────────

/**
 * One accuracy/difficulty contribution behind a recorded {@link ToHitStep}'s
 * `statusAccuracy` aggregate. Mirrors one `BoundModifier` (`@lancer-vtt/rules`'s
 * `effects/modifiers.ts`) that actually folded — a modifier whose `match`/`condition`
 * failed never produces one of these.
 */
export type AccuracyContribution = {
  /**
   * The source entity's **raw id/name** — a weapon tag id (`"tg_accurate"`), a
   * status name (`"Impaired"`, `"Lock On"`), a system id, or a pilot-effect/talent
   * id (`BoundModifier.name`). Deliberately the raw id, not a display name: this
   * step is a pure record, and resolving `"tg_accurate"` → "Accurate" for the
   * table is the render tier's job (mirrors `ResultCard`'s `nameOf` resolver for
   * combatant ids — same split, applied to modifier sources).
   *
   * **The one named exception: the disclosure sentinels.** When this contribution's
   * {@link carrier} is a class-disclosure NPC and the name was one of that NPC's hidden
   * weapons/features, `EncounterRoom.broadcastResolution` rewrites it to
   * {@link UNDISCLOSED_WEAPON_NAME} / {@link UNDISCLOSED_FEATURE_NAME} before either broadcast
   * sink. Those two strings are the ONLY non-id values this field ever holds, they are written
   * only there, and a reader must not assume an id grammar because of it.
   */
  name: string;
  /**
   * The combatant this contribution's modifier is bound to (`BoundModifier.carrier`)
   * — ordinarily the attacker for an attacker-side contribution or the target for a
   * target-side one, but a `confer`'d contribution's carrier is the *conferring*
   * ally, not the attacker/target itself (e.g. `t_spotter:1`'s Particularize).
   */
  carrier: CombatantId;
  /** Which of `statusAccuracy`'s two buckets this contribution summed into. */
  side: "attacker" | "target";
  /**
   * The signed accuracy/difficulty this one contribution added
   * (`BoundModifier.effect.mod.attackerAccuracy` / `targetAccuracy`). Never `0` —
   * `foldModifiers` only records a contribution when its facet is truthy, the same
   * gate that already decides whether it counts toward the aggregate.
   */
  value: number;
};

/**
 * One accuracy/difficulty contribution behind a recorded {@link SaveStep}'s rolled
 * save — the save/check analogue of
 * {@link AccuracyContribution}, and deliberately a **separate type rather than a
 * reuse with a dead field**: the save/check dimension is *carrier-self*
 * (`ModifierContribution.saveCheckBonus` accumulates side-agnostically, because a
 * save is rolled by one combatant and has no attacker/target axis), so
 * `AccuracyContribution`'s `side` has no meaning here and carrying it as a
 * permanently-`"attacker"` filler would misdescribe the mechanic.
 *
 * Mirrors one `BoundModifier` (`@lancer-vtt/rules`'s `effects/modifiers.ts`) whose
 * `saveCheckBonus` facet actually folded — a modifier whose `match`/`condition`
 * failed, or whose save facet `scopeSaveModifiers` stripped as off-cause/off-stat,
 * never produces one of these.
 */
export type SaveContribution = {
  /**
   * The source entity's **raw id/name** — a status name (`"Bolster"`,
   * `"Impaired"`), a system id (`"ms_stable_structure"`), or a pilot-effect/talent
   * id (`"t_ace:1"`, `"t_bonded:1"`) — i.e. `BoundModifier.name`. Deliberately the
   * raw id, not a display name: this step is a pure record, and resolving it for
   * the card is the render tier's job (same split as {@link
   * AccuracyContribution.name}).
   *
   * **Same one exception, same writer**: when this contribution's {@link carrier} is a
   * class-disclosure NPC and the name was one of that NPC's hidden weapons/features,
   * `EncounterRoom.broadcastResolution` rewrites it to {@link UNDISCLOSED_WEAPON_NAME} /
   * {@link UNDISCLOSED_FEATURE_NAME} before either broadcast sink — see
   * {@link AccuracyContribution.name}.
   */
  name: string;
  /**
   * The combatant this contribution's modifier is bound to
   * (`BoundModifier.carrier`) — ordinarily the saving combatant itself, but a
   * `confer`'d contribution's carrier is the *conferring* ally (t_bonded:1's
   * Bondmate granting +1 on the partner's saves), which is what lets the card
   * credit it `via <ally>` instead of presenting it as the roller's own.
   */
  carrier: CombatantId;
  /**
   * The signed accuracy/difficulty **count** this one contribution added
   * (`BoundModifier.effect.mod.saveCheckBonus`) — `+1`/`-1`/`+2`, NOT the rolled
   * d6 magnitude. Never `0`: `foldModifiers` only records a contribution when the
   * facet is truthy, the same gate that decides whether it counts toward the
   * aggregate. See {@link SaveStep.accuracyContributions} for how the two relate.
   */
  value: number;
};

/**
 * The recorded to-hit roll (a *record-only* step — it mutates no state, it
 * witnesses the roll). Carries the raw d20, the net accuracy/difficulty
 * contribution, the flat attack bonus, the computed total, which defense it was
 * checked against, that defense's value, and the outcome.
 *
 * `result`:
 *   - `"miss"` — `total < defense`.
 *   - `"hit"`  — `total >= defense` and `total < 20`.
 *   - `"crit"` — `total >= defense` and `total >= 20` (Lancer: "On a roll of 20+
 *     you perform a critical hit"; a crit must still hit). See `resolve`.
 */
export type ToHitStep = {
  op: "to_hit";
  /** The attacker (for the log / play-by-play). */
  attacker: CombatantId;
  /** The target whose defense was checked. */
  target: CombatantId;
  /** The raw d20 face (before bonus / accuracy). */
  d20: number;
  /** The signed accuracy/difficulty die contribution (+acc / −diff / 0). */
  accuracy: number;
  /**
   * The status contributions to the **net accuracy** that produced `accuracy`,
   * recorded so the log is auditable — the math is never silently folded into one
   * number. `intent` is the attack's own requested
   * accuracy/difficulty; `attacker` is the signed sum from the *attacker's*
   * statuses (e.g. Impaired = −1); `target` is the signed sum from the *target's*
   * statuses (e.g. Lock On = +1); `cover` is the terrain cover's signed contribution
   * (−1 soft / −2 hard), omitted when no cover applies; `weapon` is the FIRING
   * weapon's own frozen innate Accuracy/Difficulty (`WeaponProfile.accuracy`, RAW
   * Core p.281 — e.g. the Anti-Materiel Rifle's printed ACCURACY 2), omitted when
   * the weapon carries none. `weapon` is additive to, and never a substitute for,
   * `intent` (the GM's own `--accuracy` flag) — the two are independent sources.
   * The net `intent + attacker + target + cover + weapon` is the accuracy/difficulty
   * count that was rolled into `accuracy`. Optional — a no-modifier attack omits it.
   * **`attacker`/`target` are aggregate buckets — see {@link accuracyContributions}
   * for the per-source breakdown that sums to them**: the aggregate alone can't
   * tell a player *which* modifier changed their roll, only that one did.
   */
  statusAccuracy?: {
    intent: number;
    attacker: number;
    target: number;
    cover?: number;
    weapon?: number;
  };
  /**
   * The per-source breakdown behind {@link statusAccuracy}'s `attacker`/`target`
   * aggregates — one entry per `BoundModifier` whose
   * `attackerAccuracy`/`targetAccuracy` facet actually folded (its
   * `match`/`condition` held; `foldModifiers` `continue`s past a failed one before
   * it ever reaches this list, so a gated modifier that didn't apply this resolution
   * never appears here). Filtering by `side` and summing `value` reproduces
   * `statusAccuracy.attacker`/`.target` exactly — this is strictly additional
   * provenance, not a second, differently-derived total. Omitted entirely when the
   * list would be empty (a no-modifier attack), never `[]`.
   */
  accuracyContributions?: AccuracyContribution[];
  /** The flat to-hit bonus added (the attacker's `attackBonus`). */
  bonus: number;
  /** `d20 + bonus + accuracy`. */
  total: number;
  /** Which defense the total was checked against. */
  vs: "evasion" | "edef";
  /** That defense's value on the (frozen) target — already capped, if {@link evasionCap} applied. */
  defense: number;
  /**
   * **Stunned's Evasion cap** (RAW Core p. 78: "max 5
   * EVASION") — the cap value, present iff a target-side `evasionCap` modifier was active
   * on this resolution (weapon attacks only; `defense` already reflects `min(evasion, cap)`
   * on that vs `"evasion"`, never on a tech attack's `"edef"`). Omitted when no such
   * modifier applied — the common case.
   */
  evasionCap?: number;
  result: "miss" | "hit" | "crit";
  /**
   * The terrain **cover degree** the target had (measured from the attacker's
   * footprint, or the area template's origin for a Blast)
   * that was folded into `accuracy`, for the result card ("+1 Diff (soft cover)" /
   * "+2 Diff (hard cover)"). Omitted when the attack ignores cover entirely (melee,
   * tech, Seeking) or the live map reports no cover on the line — never `"none"`,
   * so presence of the key is itself "cover applied."
   */
  cover?: "soft" | "hard";
  /**
   * **Advantage**, an attack-roll replacement (e.g. t_spotter:1's
   * Particularize: "…they may roll twice and choose either result"). Present iff this
   * to-hit rolled WITH ADVANTAGE: the value of the SECOND, non-kept d20 draw (`d20`
   * already carries the kept/higher one — the ordinary field every reader already
   * understands, so an advantage roll degrades gracefully for any reader that doesn't
   * know this field). Omitted when no advantage replacement applied (the common
   * case) — an ordinary single-d20 roll.
   */
  advantageDrop?: number;
  /**
   * **An automatic miss** (`ll1-granted-reactions` S2): a declared-window reaction (Stasis Bolt's
   * Interdiction Point) made this attack miss before it was rolled. No d20 was drawn: `d20` and
   * `accuracy` read `0`, `total` is the bare `bonus`, and `result` is `"miss"`. `interdictedBy`
   * names the reacting combatant. Omitted on every rolled attack.
   */
  forcedMiss?: { interdictedBy: CombatantId };
  /**
   * **An automatic hit** (`ll1-granted-reactions` 9.1): a `forced_attack` with `autoHit` (the
   * Autopod's Autonomous Assault). No d20 was drawn: `d20` and `accuracy` read `0`, `total` is the
   * bare `bonus`, and `result` is `"hit"`, never a crit. Omitted on every rolled attack.
   */
  autoHit?: true;
  /**
   * **How this attack looks on the map** (`attack-animations` Phase 1): the animation family the
   * firing weapon's own data picks (`attackFx` in `@lancer-vtt/rules`), its area, its palette and
   * the thrown/Smart modifiers. Record-only and presentation-only: nothing in the rules reads it.
   * It names a kind of weapon, never the weapon, so class-tier redaction leaves it alone
   * (`decisions/0081`, ruled 2026-10-10). Absent on a hand-built fixture; the map then flies its
   * default tracer.
   */
  fx?: AttackFx;
}

/**
 * The recorded damage roll (a *record-only* step — the grounded HP/burn changes
 * ride separate steps). The step records the full, auditable **typed damage +
 * mitigation** computation so the play-by-play and replay never recompute.
 *
 * The mitigation composition recorded here is the rulebook order ("CALCULATING
 * DAMAGE", core book): roll → subtract armor (skipped entirely if the weapon
 * is AP or the damage type is burn/heat, which ignore armor) → resistance halves
 * (rounded up, Lancer golden rule II) → floor at 0. A single attack's armor
 * applies once.
 *
 * Every field is the *recorded outcome*, not a re-derivable input:
 *   - `dtype` — the damage type (drives resistance + armor-ignore).
 *   - `dice` — the kept damage die faces (with overkill, the settled faces).
 *   - `bonus` — the weapon's flat damage bonus (the `+3` in `1d3+3`).
 *   - `preMitigation` — `sum(dice) + bonus` (damage before any reduction).
 *   - `ap` — whether the weapon was armor-piercing (armor was skipped).
 *   - `armor` — the armor amount actually subtracted (`0` if AP / burn / no armor).
 *   - `resisted` — `true` if resistance halved it. Resistance itself IS wired
 *     (`decisions/0171`: `foldedResistance` folds `modifier { resistance }` clauses off
 *     every carrier vector — statuses, pilot/frame/gear/npc-feature ids, equipped systems —
 *     with the real attacker threaded in for attacker-relative grants like Point-Defense
 *     Shield or Siege Armor), but the fold only returns a **boolean** (`ModifierContributionSum
 *     .resistance` sums bare `"all" | DamageType` tokens, dropping which carrier granted it)
 *     — there is no attributable source to stamp here yet, so `resisted` stays a bare flag
 *     with no accompanying name/id, unlike {@link source} below. A future fold that retains
 *     the granting carrier could add a `resistSource` alongside this field the same way.
 *   - `final` — the post-mitigation damage actually dealt to HP (floored at 0).
 * `crit` records whether the damage was crit-doubled (kept-higher-half).
 *
 * **Provenance (`target`/`source`, `resolution-narration` 1.2):** who this damage was rolled
 * against and where it came from, so the receipt can name it beside its number — modelled on
 * {@link AccuracyContribution}. Both optional and additive: pre-1.2 `feed.jsonl` entries have
 * neither, and the narrator (1.3) degrades gracefully ("attacks" / "bonus damage") rather than
 * crashing. **Not every emission site stamps these yet** — the single-target weapon-attack path
 * (`damageApply.ts`'s `op: "damage"` sites) and the `extra_damage`/`interpretDamage` rider paths
 * (`effects/interpret.ts`) do; `mine.ts`, `tables.ts`'s structure/destruction cascade, and
 * `bulldoze.ts` do not (Phase 1 is the single-target weapon attack; those are out of scope).
 */
export type DamageStep = {
  op: "damage";
  /** The damage type (kinetic/energy/explosive/burn/heat/variable). */
  dtype: DamageType;
  /** Every kept damage die face, in order (overkill: the settled, non-1 faces). */
  dice: number[];
  /**
   * The damage die's side count (the `M` in the weapon's `Nd`M`) — for the dice overlay's
   * mesh choice (a d3 weapon renders a d3 solid, not a d6). Recorded by the resolver when a
   * real dice pool is rolled. **Optional**: a dice-less damage step (Reliable/flat-NPC/the
   * `extra_damage` atom, all `dice: []`) omits it; the overlay defaults to d6 (Lancer's
   * common damage die) when absent.
   */
  sides?: number;
  /** The weapon's flat damage bonus added to the dice (the `+3` in `1d3+3`); `0` if none. */
  bonus: number;
  /** `sum(dice) + bonus` — damage before mitigation — or the weapon's Reliable floor when that is higher. */
  preMitigation: number;
  /**
   * The weapon's Reliable X, present only when it lifted a hit's rolled `sum(dice) + bonus` up to X
   * (Core, the RELIABLE tag). A miss's floor step records X as `bonus` with no dice instead.
   */
  reliable?: number;
  /** Whether the weapon was armor-piercing (armor skipped). */
  ap: boolean;
  /** Armor actually subtracted (`0` when AP / burn / heat / no armor). */
  armor: number;
  /** Whether resistance halved the damage. See the type doc comment above: wired, but source-less. */
  resisted: boolean;
  /** Post-mitigation damage actually dealt to HP (floored at 0). */
  final: number;
  /** Whether this was a crit (double-rolled, higher half kept). */
  crit: boolean;
  /**
   * **Immunity, damage scope**: `true` iff the target held damage-scope Immunity to `dtype`
   * at resolve time (Stay of Execution, etc.) — RAW p.68's "never takes damage, even 0
   * damage." A NO-OP marker, not a derived fact from `final === 0`: `final` still records
   * what the roll/mitigation WOULD have been (for the play-by-play/audit trail), but no
   * `hp_delta`/`burn_set`/cascade step was emitted for this hit — the damage never applied.
   * Omitted (not `false`) on every non-immune hit.
   */
  immune?: boolean;
  /**
   * The combatant this damage was rolled against, so the receipt can pair a `damage` step
   * with the target it hit without cross-referencing the resolution's `to_hit`/`about`.
   * Optional — see the type doc comment's emission-site note; a pre-1.2 step or one from an
   * unstamped site (mine/table/bulldoze) omits it.
   */
  target?: CombatantId;
  /**
   * Where this damage came from, so the receipt can name it beside its number
   * (`AccuracyContribution`'s shape, applied to damage). Optional for the same reasons as
   * {@link target}.
   */
  source?: {
    /** `"weapon"` — the firing weapon's own damage. `"rider"` — a talent/system/pilot-effect
     * bonus damage atom (`extra_damage`) riding the same attack, or an activation's own
     * `extra_damage` (a thrown grenade), which names the activated item's frozen display name
     * (`narration-ledger-retirement` 2.3). `"effect"` — a non-rider source: today only the GM's
     * adjudicated damage, whose witness step names `"GM"` (`refereeGmDamage`, 4.1). */
    kind: "weapon" | "rider" | "effect";
    /**
     * Display name when the emitter has one, else the raw id — and **which one depends on
     * `kind`, not a per-step choice**: a `"weapon"` source's `name` is always the WeaponProfile's
     * real display name (`"Anti-Materiel Rifle"`) — a genuine title, hyphens and all, never
     * transform it (an id-titling pass over it would corrupt "Anti-Materiel" at the hyphen). A
     * `"rider"` source's `name` is the raw carrying-entity id (`"ms_hunter_lock"`,
     * `"t_crack_shot:2"`) — there is no compendium lookup at record time to resolve it to a
     * display name, so 1.3's narrator titles THIS one (id → display), never a weapon's.
     *
     * **The one named exception to both halves: the disclosure sentinels.** When this source
     * belongs to a class-disclosure NPC — its own {@link carrier}, or the attacker of the
     * segment's `to_hit` for a carrier-less source — and it named one of that NPC's hidden
     * weapons/features, `EncounterRoom.broadcastResolution` rewrites this field to
     * {@link UNDISCLOSED_WEAPON_NAME} / {@link UNDISCLOSED_FEATURE_NAME} and **drops `id`**
     * beside it, once, viewer-agnostically, before either broadcast sink. Both sentinels
     * contain a space, so the narrator's display-name guard passes them through verbatim on
     * every row. They are the ONLY non-id/non-display-name values this field holds.
     */
    name: string;
    /** The raw compendium/talent/system id, when known (`"ms_hunter_lock"`, `"t_crack_shot:2"`).
     * **Dropped by the disclosure redaction** whenever {@link name} becomes a sentinel — see
     * that field's doc. */
    id?: string;
    /**
     * The combatant whose effect this was, when it is NOT the attacker — a rider conferred by
     * an ally. Same meaning as {@link AccuracyContribution.carrier}. Omitted for the
     * attacker's own weapon/rider. **Deliberately excluded from {@link
     * stepCombatantIds}'s fog-of-war/feed-suppression read** (`resolutionSteps.ts`), mirroring
     * `to_hit`'s `accuracyContributions[].carrier` — see that function's comment.
     */
    carrier?: CombatantId;
  };
}

/**
 * A **grounded** step: change a combatant's current HP by `delta` (negative =
 * damage). Self-inverting — its inverse is the same step with `delta` negated — so
 * undo is closed over the step. `applyResolved` clamps the result to `[0, maxHp]`
 * exactly as the manual `set_hp` does; the inverse records the delta, not the
 * absolute, so a sequence of deltas round-trips.
 *
 * **Clamping vs. round-trip:** because the forward fold clamps, the *naive* inverse
 * (`+delta`) only round-trips when the forward step didn't hit a bound. The step
 * carries `delta`, and **the resolver is responsible for that being the *effective*
 * delta — what actually changed — whenever clamping can bite**. The `apply_resolved`
 * reducer backstops it: its inverse negates each delta as it landed (`applyResolvedLanded`),
 * so a stale emitter misplays forward but never corrupts undo.
 *
 * **No post-apply HP field (`resolution-narration` 1.2, considered and deliberately deferred):**
 * the obvious addition — an `after` carrying the target's post-apply HP, so the receipt's
 * `RESULT` row could read "HP 20 → 4" instead of stopping at "takes 16" — turns out to be
 * unsafe to compute at any single emission site. Every site that would populate it
 * (`hpDeltaStep`, `resolveDamageCascade`'s `hpDelta`, the `set_hp` atom) reads `target.current
 * .hp`, which is the RESOLUTION-ENTRY snapshot — correct when a target takes exactly one
 * `hp_delta` in the whole resolution, but WRONG the moment a second one lands on the SAME
 * target later in the SAME resolution (a weapon hit plus a talent/system rider, e.g. real
 * `ms_hunter_lock` content: `applyHitDamage`'s per-instance loop and the on-hit trigger-bus
 * fire each started fresh from the frozen `target`, so the rider's own naive `after` would
 * repeat "entry HP − its own delta" instead of the true post-BOTH-hits figure — proven against
 * `packs/core-rulebook/entries/systems/hunter-lock.test.ts` while building this; both now fold
 * the earlier steps first, BC-016, but the other emission sites still read the entry snapshot). The step's own `delta` stays safe to fold sequentially (`applyResolved`
 * re-clamps on every apply, so the recorded deltas always land on the correct real final
 * state) — only a RECORDED "after" literal, read at face value with no re-clamp, would be
 * wrong. Fixing it for real means threading a running-HP accumulator through {@link
 * import("../../../rules/src/encounter/resolve/damageApply").applyHitDamage}'s instance loop →
 * `applyDamageInstance` → `routeFinalDamage` → `resolveDamageCascade` — real engine plumbing,
 * not a "record where it's already computed" addition, so it stayed out of 1.2. Per the
 * tracker's own stated fallback for exactly this finding, the `RESULT` row stops at "takes N
 * damage"; the post-apply HP is a follow-up (Phase 4 or its own tracker).
 */
export type HpDeltaStep = {
  op: "hp_delta";
  target: CombatantId;
  /** Signed change to current HP (negative = damage). */
  delta: number;
}

/**
 * A **grounded** step: change a combatant's current **heat** by `delta` (e.g. an
 * overkill reroll dealing heat to the *wielder*). Self-inverting
 * (negate `delta`). `applyResolvedStep` clamps the result to `[0, maxHeat]` (the
 * same bound `set_heat` enforces) and is a **no-op on a combatant with no heat
 * track** (`maxHeat === undefined` — non-mech kinds), so heat steps are total over
 * any combatant. As with `hp_delta`, the resolver records the **effective** delta
 * (what actually changes after clamping) so the inverse round-trips at the cap.
 */
export type HeatDeltaStep = {
  op: "heat_delta";
  target: CombatantId;
  /** Signed change to current heat (positive = gain). */
  delta: number;
}

/**
 * A **grounded** step (rulebook "STRUCTURE DAMAGE"): change a combatant's current
 * **structure** by `delta` (negative = took structure damage). Self-inverting
 * (negate `delta`). `applyResolvedStep` clamps the result to `[0, maxStructure]`
 * and is a **no-op on a combatant with no structure track** (`maxStructure` absent —
 * pilots/objects). As with `hp_delta`, the resolver records the **effective**
 * delta so the inverse round-trips at a bound.
 */
export type StructureDeltaStep = {
  op: "structure_delta";
  target: CombatantId;
  /** Signed change to current structure (negative = structure damage taken). */
  delta: number;
}

/**
 * A **grounded** step (rulebook "OVERHEATING AND STRESS"): change a combatant's
 * current **stress** by `delta` (negative = took stress damage). Self-inverting
 * (negate `delta`). `applyResolvedStep` clamps to `[0, maxStress]` and is a
 * **no-op on a combatant with no stress track** (`maxStress` absent).
 */
export type StressDeltaStep = {
  op: "stress_delta";
  target: CombatantId;
  /** Signed change to current stress (negative = stress damage taken). */
  delta: number;
}

/**
 * A **record-only** step: the witness of the **structure-loss interception window**
 * (`packages/rules/src/encounter/structureIntercept.ts` — Custom Paint Job is the one fielded
 * offer). It mutates nothing: the *consequences* of a successful interception ride as separate
 * grounded steps in the same list (the `affordance_use_set` charge spend, the `hp_delta` restore,
 * and the compensating `structure_delta +1` the cascade pushes to give the point back), exactly
 * the way {@link TableCheckStep} witnesses a table roll whose consequences are separate steps. So
 * it inverts to nothing, and undo of the whole resolution reverses those grounded steps.
 *
 * **Why it exists (`resolution-narration` 3.3).** The window's own d6 lived only in advisory
 * `warnings` prose, so no consumer of the record could say *why* a mech that should have lost a
 * structure point did not — or, on a failed roll, that the charge was spent at all. The developer's
 * standing rule is that a player never wonders whether an ability fired, and the narrator renders
 * off the durable feed with no encounter state loaded, so the fact has to be on the record or it
 * cannot be said. Same posture as {@link DamageStep}'s `target`/`source`: record now, read later.
 * **The existing `warnings` text is unchanged** — a `--json`-tier consumer sees exactly what it saw
 * before; this step is additive.
 *
 * **When it is emitted.** On a recorded answer only:
 *   - **fired** — `roll` carries the die face, `ignored` says whether it made the threshold. Pushed
 *     inside the offer's `outcome` *after* the die is drawn (that ordering is load-bearing; see the
 *     draw-site comment in `structureIntercept.ts`).
 *   - **declined** — `declined: true`, `ignored: false`, and **no `roll`**: the offer was raised,
 *     a decider answered "save", and no die was drawn.
 * A NON-interactive cascade (replay / CLI / the AI tier) that defaults to decline without ever
 * raising a prompt emits **nothing** — nobody was offered anything, so there is nothing to report,
 * and that keeps a non-interactive cascade byte-identical to one with no window at all.
 */
export type InterceptStep = {
  op: "intercept";
  /** The combatant whose structure loss was offered up for interception. */
  target: CombatantId;
  /**
   * Which ability offered. `name` is a genuine **display name** ("Custom Paint Job") — authored on
   * the offer beside its spec, never an id run through a titling transform (the same rule
   * {@link DamageStep.source}'s `"weapon"` kind carries). `id` is the compendium item id whose
   * authored clause grants the ability (`"ms_custom_paint_job"`).
   */
  source: { name: string; id: string };
  /** The die face rolled. **Absent on a decline** — a declined offer draws nothing. */
  roll?: number;
  /** Faces on the die the offer rolls (Custom Paint Job: 6). Recorded so a reader can say "d6" without hardcoding it. */
  sides: number;
  /** The roll succeeds at `roll >= threshold`. Recorded on a decline too ("offered, declined — would have needed a 6"). */
  threshold: number;
  /** Did the interception un-take the structure point ("…and ignore the damage")? `false` on a failed roll and on a decline. */
  ignored: boolean;
  /** The offer was raised and a decider answered "save": no charge spent, no die drawn. Omitted (not `false`) when it fired. */
  declined?: true;
}

/**
 * A **record-only** step: the witness of an **Overheating** or
 * **Structure Damage** table check. Like {@link ToHitStep}/{@link SaveStep} it mutates
 * no state — it records that the 1d6-per-marked-point pool was rolled, the lowest
 * value, how many 1s came up, and which table row resolved; the row's *consequences*
 * (stress/structure delta, a status, a meltdown countdown, destruction) ride as
 * **separate grounded steps** in the same list. So it inverts to nothing, and undo of
 * the whole resolution reverses the row's grounded steps.
 *
 * `kind` distinguishes the two tables (both share the lowest-of-1d6-per-point shape).
 * `row` is the resolved table-row label (e.g. "Emergency Shunt", "Direct Hit"), for the
 * play-by-play. `dice` are every d6 face rolled, `lowest` the chosen value, `ones` the
 * count of 1s (a multiple-1s catastrophic result). `track` records the remaining track
 * value (stress/structure) the branch read, when a row's outcome depended on it.
 */
export type TableCheckStep = {
  op: "table_check";
  /** Which consequence table was rolled. */
  kind: "overheating" | "structure_damage";
  /** The combatant making the check. */
  target: CombatantId;
  /** Every d6 face rolled (one per marked point, including the point just taken). */
  dice: number[];
  /** The chosen (lowest) value the row was read from. */
  lowest: number;
  /** Count of 1s rolled (≥2 = the catastrophic multiple-1s row). */
  ones: number;
  /** The remaining track value the branch read (stress/structure after the just-taken point), when relevant. */
  track?: number;
  /** The resolved table-row label (for the play-by-play). */
  row: string;
  /**
   * The title of the table rolled when it is not the core one for `kind` — a pack's
   * `FrozenStructureTable` ("Monstrosity Structure Damage"). Absent for the core tables.
   */
  table?: string;
}

/**
 * A **record-only** step (RAW Core p.71 / SRD combat.json: "any time a mech with the AI tag
 * makes a structure damage or overheating check, roll 1d20. On a 1 the NHP cascades"): the
 * witness of the **cascade check** ridden inside `resolveOverheating`/
 * `resolveStructureDamage` (`@lancer-vtt/rules`' `tables.ts`) — one roll per structure/overheat
 * CHECK event (never per-hit, never per-turn; a multi-structure-marking hit rolls once per
 * System Trauma check via `resolveDamageCascade`'s per-check call site). Mirrors {@link
 * TableCheckStep}'s record-only shape: it mutates no state — on a natural 1 the *consequence*
 * (the NHP going rogue) rides a **separate** grounded `status_add "Cascading"` step alongside
 * this witness, so undo of the whole resolution reverses that grounded step, not this one.
 *
 * Only emitted when the checking combatant carries ≥1 **active cascade source** — an equipped
 * system tagged `tg_ai` and NOT `tg_no_cascade` (re-derived live off `base.equippedSystems` at
 * check time — never a spawn-frozen flag); a mech with no AI system, or only a No-Cascade-
 * exempt one (a comp/con-class assistant unit self-exempts by carrying both tags), never
 * rolls at all and emits no witness.
 *
 * `d20` is the raw face rolled (no bonus/accuracy — this is a flat 1-in-20, not an attack/save);
 * `cascaded` is `d20 === 1` (kept as an explicit field, not re-derived at every read site).
 */
export type AiCascadeCheckStep = {
  op: "ai_cascade_check";
  /** The combatant making the check. */
  target: CombatantId;
  /** The raw d20 face rolled. */
  d20: number;
  /** `true` iff `d20 === 1` — the NHP cascades (a `status_add "Cascading"` rides alongside). */
  cascaded: boolean;
}

/**
 * A **record-only** step marking a **causal boundary** inside one resolution
 * (`chained-resolution-sequence`, `decisions/0210`): from here to the next `link` (or the end of
 * the list), a different unit acts because of something earlier in the same resolution. The
 * game-night chain is the worked example: Bret's Shotgun hits the Berserker, and the Berserker's
 * Aggression forces a Chain Axe swing at a randomly picked neighbour. The swing's steps follow a
 * `link { cause: "forced_attack", actor: Berserker, feature: Aggression, pick: d3 = 1 }`.
 *
 * The steps before the first `link` are the resolution's own primary action (`about` describes
 * it). Structure, stress and overheat checks and a destroy carry no marker: they sit inside the
 * link whose damage caused them. Trailing engine bookkeeping (`attack_ordinal_this_turn`) may
 * follow the last link; no reader renders it.
 *
 * **Inline, not index ranges on `about`,** because the per-socket fog filter drops steps, which
 * would shift any recorded index. An inline marker survives filtering and the class-tier
 * redaction walk (which rewrites {@link feature} and {@link weapon} like a damage source).
 *
 * Folds as a no-op in `applyResolved`, like `to_hit`, so replay and undo are unchanged.
 */
export type LinkStep = {
  op: "link";
  /**
   * Why this link happened. `forced_attack`: a `forced_attack` atom's nested attack (Aggression,
   * Avalanche Charge, Rebound's repeat, Punishment's Overwatch). `drone_field`: a drone field
   * holding a forced attacker fired before its attack (Sentinel, `decisions/0201` D5).
   * `reaction` (`chained-resolution-sequence` 4.3): a reaction or automatic clause that fired in
   * the middle of an attack (Brace, Rebound's resist, a target's on-damage clause); its steps run
   * to the matching `resume`. `resume`: the interrupted attack carries on, its `actor` the attack's
   * attacker; the steps after it belong to the link the reaction interrupted.
   */
  cause: "forced_attack" | "drone_field" | "reaction" | "resume";
  /** The combatant acting in this link. */
  actor: CombatantId;
  /** The combatant the link's action targets, when it has one. */
  target?: CombatantId;
  /**
   * The feature whose clause caused the link (`npcf_aggression_berserker`), as the firing
   * clause recorded it (a raw id or a display name; readers title it with `displaySourceName`),
   * and the combatant carrying it. Absent when nothing named fired it.
   */
  feature?: { name: string; id?: string; carrier: CombatantId };
  /** The weapon the link's attack uses, by display name (class-tier redaction applies). */
  weapon?: { name: string; id?: string; melee?: true };
  /**
   * The random pick that chose {@link target} (`choose_random`): the candidates in pick order
   * and, when there was more than one, the die face drawn (`d${from.length}`).
   */
  pick?: { from: CombatantId[]; roll?: number };
  /** The link repeats the triggering attack against {@link target} (Rebound's reflection) rather than making a fresh one. */
  repeat?: true;
};

/**
 * A **grounded** step: mark a combatant as **destroyed** (or undo it).
 * Reduced to 0 structure (or a catastrophic table row) → the mech is destroyed. Modeled
 * as a status-like flag so it folds + inverts cleanly: `applyResolvedStep` adds/removes a
 * `"Destroyed"` status instance (the destroyed marker lives in the status overlay world,
 * never a dead table). Self-inverting (`destroyed` flips). The HP/structure tracks keep
 * their values (a destroyed mech can be repaired); destruction is the carried marker.
 *
 * **`vaporized`**: set at the reactor-
 * meltdown emission site ({@link "../../../rules/src/encounter/meltdown".resolveMeltdownBlast}
 * — a mech destroyed by its own meltdown is RAW-distinct from an ordinary kill, Core p.81
 * "the wreck is annihilated"). `applyResolvedStep` stamps a **second** `"Vaporized"` status
 * beside `"Destroyed"` when this is `true` — every existing gate keyed on the `"Destroyed"`
 * name (activation exclusion, movement occupancy, object contributions, …) is untouched by
 * the extra status, and the wreck-spawn post-resolution observer reads `"Vaporized"` to skip
 * spawning a wreck for an annihilated mech. Purely additive — omitted on every ordinary
 * (non-meltdown) destroy, and on the inverse it is carried through unchanged (not stripped)
 * so a redo (invert-of-invert) rebuilds the exact original step; `applyResolvedStep`'s
 * `destroyed: false` branch clears BOTH markers unconditionally regardless of this field.
 */
export type DestroyStep = {
  op: "destroy";
  target: CombatantId;
  /** `true` = mark destroyed; `false` = clear the marker (the inverse). */
  destroyed: boolean;
  /** Destroyed by reactor meltdown — annihilated, no wreck. See the doc above. */
  vaporized?: true;
}

/**
 * A **grounded** step (Core p.80 System Trauma): mark a
 * single **frozen item** (a weapon or a system) as destroyed (or undo it) — the item-level
 * counterpart to {@link DestroyStep}, which is whole-combatant. `key` is the item's identity in
 * the shared destroyed-item namespace: a weapon's {@link import("./types").weaponInstanceKey}, or
 * a system's {@link import("./types").FrozenSystem.instanceId} — the two schemes are disjoint
 * (weapon instance ids never collide with `system:${i}`), so one flat namespace suffices.
 *
 * **Folded into `current.resources`, not a new top-level field** — mirrors the Loading
 * convention ({@link import("./types").loadedResourceKey}) rather than `discharge_set`'s
 * dedicated per-affordance ledger: a destroyed-item marker is one
 * more namespaced key in the same sparse per-instance bag Loading already uses, read via
 * {@link import("./types").destroyedItemResourceKey}/{@link import("./types").isItemDestroyed}.
 * Presence (value `1`) means destroyed; absence means intact — the same "sparse-absent-means-
 * pristine" convention every other `current.resources` key follows.
 *
 * Self-inverting (`destroyed` flips), exactly like {@link DestroyStep}/{@link DischargeSetStep}.
 * A destroyed weapon takes its mod down implicitly, and that needs no separate step: a mod has
 * no independent existence in any participation gate, so nothing else need fold on it.
 */
export type DestroyItemStep = {
  op: "destroy_item";
  target: CombatantId;
  /** The destroyed item's key — a weapon's instance key, or a system's frozen `instanceId`. */
  key: string;
  /** `true` = mark destroyed; `false` = clear the marker (the inverse). */
  destroyed: boolean;
}

/**
 * A **grounded** step: set a combatant's **marked burn** to an absolute value
 * (rulebook "BURN"). Absolute (not a delta) because the
 * resolver computes the new marked total (old + newly-applied, or `0` on a tick
 * that clears it) and records it; the inverse carries the captured `prev` so undo
 * restores the exact prior mark. (A delta would suffice for adds, but the burn
 * *tick* clears to 0 from an arbitrary value — an absolute set inverts cleanly in
 * both directions.)
 */
export type BurnSetStep = {
  op: "burn_set";
  target: CombatantId;
  /** The new marked-burn value. */
  burn: number;
  /** The marked-burn value before this step (for the inverse). */
  prev: number;
}

/**
 * A **grounded** step: add a status/condition name to a combatant. Mirrors the
 * manual `apply_status` command, but recorded into a resolution's step list so a
 * *resolution* (not just a GM fiat) can apply a status — a tech action's Lock On,
 * or any resolved effect that lands a condition. Self-inverting against
 * {@link StatusRemoveStep}.
 *
 * **Idempotent at playback** (`applyResolvedStep`): adding a status the target
 * already carries is a no-op, and removing one it lacks is a no-op — replay never
 * re-validates or throws (the authority validated at resolve time), so a step list
 * stays total even if state drifted. The resolver only emits these when they
 * change something, so the common path round-trips exactly.
 */
export type StatusAddStep = {
  op: "status_add";
  target: CombatantId;
  /** The status/condition name to add (free string — same space as `apply_status`). */
  status: string;
  /**
   * The added instance's **expiry descriptor**. Optional —
   * a GM-applied status omits it and playback defaults
   * to `{ kind: "until_removed" }` (the descriptive manual-tier behavior). A resolved
   * effect that lands a timed status (Concussion Missiles → Impaired, a `target`+`end`
   * `turn` expiry) sets it so the instance carries its duration.
   */
  expiry?: StatusExpiry;
  /** The combatant that applied it (for a `source`-keyed `turn` expiry); omitted if GM-applied. */
  source?: CombatantId;
  /**
   * The added instance's {@link StatusInstance.appliedTurn}. Optional, and **never defaulted
   * at playback** — the `status_add` case carries it through `makeStatusInstance` verbatim,
   * whose doc says why (`decisions/0132`). An emitter that wants the stamp sets it EXPLICITLY
   * at emission (mirrors `interpretCreateArea`'s unconditional `AreaEffect.appliedTurn`
   * stamp); omitting it leaves the instance unstamped, and its `turn`-kind expiry (if any)
   * never skips the application turn.
   */
  appliedTurn?: number;
  /**
   * The **paired combatant** the added instance points at — carried straight onto the
   * {@link StatusInstance} of the same name; see that field's doc for why this is distinct
   * from {@link source}. Purely additive; omitted on every non-paired status add.
   */
  partner?: CombatantId;
  /**
   * The added instance's {@link StatusInstance.weapon} — the triggering weapon instance key
   * a follow-up reservation is pinned to, carried straight onto the {@link StatusInstance}.
   * Purely additive; omitted on every status add that isn't a `reserve_followup`.
   */
  weapon?: string;
  /**
   * The added instance's {@link StatusInstance.cause} — carried straight onto the instance.
   * Purely additive; omitted on every non-tech-caused status add.
   */
  cause?: "tech";
  /**
   * The added instance's {@link import("./types").StatusInstance.ruling} — the GM's ad-hoc
   * ruling, carried straight onto the instance. Purely additive;
   * omitted on every status add that isn't a `grant_ruling` (or the undo of one). **The
   * carry-through is what makes a burned ruling restorable**: a `consumeOnAttack` ruling
   * self-removes via a recorded `status_remove` step, and an undo of that attack rebuilds
   * the instance from this field — drop it and the chip comes back mechanically empty,
   * silently and forever.
   */
  ruling?: RulingSpec;
  /** The added instance's {@link StatusInstance.endsOn}, carried straight onto the instance. */
  endsOn?: EventEnd[];
}

/**
 * A **grounded** step: remove a status/condition name from a combatant. The inverse
 * of {@link StatusAddStep}. The canonical emitter is **Lock On consume** — an attack
 * that hits a Locked-On target records a `status_remove "Lock On"` so the consumed
 * lock is undoable and replayable (see the resolver's Lock-On handling). Idempotent
 * at playback (removing an absent status is a no-op).
 */
export type StatusRemoveStep = {
  op: "status_remove";
  target: CombatantId;
  /** The status/condition name to remove. */
  status: string;
  /**
   * The removed instance's expiry/source/partner/cause, carried **only on an inverse-of-add**
   * (or a direct multi-instance removal that already has the live instance in hand — Shut
   * Down's "end every tech-caused status") so the round-trip is byte-identical: undoing a
   * `status_add` that set an `expiry` must, when *re*done, restore that exact expiry — so the
   * `status_remove` inverse of a timed `status_add` records the instance's facets, and *its*
   * inverse rebuilds the full instance. A directly-emitted `status_remove` (Lock On consume)
   * omits them; its inverse re-adds the default `until_removed`, which is correct for a
   * consumed manual-tier status.
   */
  expiry?: StatusExpiry;
  source?: CombatantId;
  /**
   * The removed instance's {@link StatusInstance.appliedTurn}, carried on the SAME
   * inverse-of-add / live-instance-in-hand basis as
   * {@link expiry} above — so a further redo (turning this back into a `StatusAddStep`)
   * restores the exact original application turn, not whatever `turnOrdinal` happens to be
   * live at redo time.
   */
  appliedTurn?: number;
  /** The removed instance's {@link StatusInstance.partner}, carried on an inverse-of-add. */
  partner?: CombatantId;
  /** The removed instance's {@link StatusInstance.weapon} (follow-up reservation pin), carried on an inverse-of-add. */
  weapon?: string;
  /** The removed instance's {@link StatusInstance.cause}, carried on an inverse-of-add. */
  cause?: "tech";
  /**
   * The removed instance's {@link StatusInstance.ruling}, carried on
   * the same inverse-of-add / live-instance-in-hand basis as {@link expiry}. **The
   * `consumeOnAttack` self-removal is a live-instance-in-hand emitter, not an inverse** — the
   * compiled `remove_status` atom must record the ruling it is burning, or undoing that attack
   * restores a chip with no mechanics.
   */
  ruling?: RulingSpec;
  /** The removed instance's {@link StatusInstance.endsOn}, carried on the same basis as {@link expiry}. */
  endsOn?: EventEnd[];
}

/**
 * A **grounded** step (the `pause_status_expiry` atom — ms_stay_of_execution's "any active
 * effects on your target are frozen in time"): set a named status
 * instance's {@link StatusInstance.suspended} flag to an absolute value, mirroring {@link
 * MineArmedSetStep}/{@link ConcealedSetStep}'s absolute-with-`prev` shape (self-inverting by
 * swapping `suspended`/`prev`). Suspending an instance freezes its expiry timer — the duration
 * bus's `expiresAt` returns `false` unconditionally while `suspended` is `true`, regardless of
 * which lifecycle event fires — and un-suspending resumes normal ticking (it expires at the
 * next matching lifecycle event, exactly as if it had never been paused). Targets a status by
 * **name** (the same dedup key `StatusAddStep`/`StatusRemoveStep` use); a target with no live
 * instance of that name is a no-op at playback (replay is total, mirrors every other step).
 */
export type StatusSuspendSetStep = {
  op: "status_suspend_set";
  target: CombatantId;
  /** The status/condition name whose expiry timer to (un)suspend. */
  status: string;
  /** `true` = suspend (freeze) the expiry timer; `false` = resume it (the inverse). */
  suspended: boolean;
  /** The value before this step (for the inverse). */
  prev: boolean;
}

/**
 * A **grounded** step: set a combatant's named **resource**
 * to an absolute value. Resources are the first-class primitive that subsumes
 * charges (1/round, 1/full-repair), `Limited {VAL}`, `Loading`, the Apocalypse Die
 * counter — all "a number on a combatant with a consume/reset rule".
 * `key` is a free string (the resource name, e.g. `"limited"`,
 * `"apocalypse_die"`); `val` is the absolute new value; `prev` captures the value
 * before this step for the inverse (mirrors {@link BurnSetStep} — an absolute set
 * inverts cleanly in both directions, including a reset-to-0 from an arbitrary
 * value). A resource the combatant didn't have is created by the set; the inverse's
 * `prev` of a freshly-created resource is `0` (the resolver records the effective
 * prior value, defaulting an absent resource to 0 — see `applyResolvedStep`).
 */
export type ResourceSetStep = {
  op: "resource_set";
  target: CombatantId;
  /** The resource name (free string — the key into `current.resources`). */
  key: string;
  /** The new absolute value. */
  val: number;
  /** The value before this step (for the inverse); `0` if the resource was absent. */
  prev: number;
  /**
   * `true` iff `key` was genuinely ABSENT from `current.resources` before this step
   * (not merely `0`) — the sparse-delta "absent key == pristine" convention,
   * needed for a resource whose OWN read gives absence a
   * DIFFERENT meaning than `0` (Loading's `?? 1` — "no key means loaded").
   * Ordinary resources (Limited, the Apocalypse
   * Die, …) read absence as `0` too, so `prev: 0` already round-trips correctly and
   * this flag stays unset. When set, `invertStep` produces a {@link
   * ResourceClearStep} instead of the plain val/prev swap — a fresh run and a
   * fire-then-undo run must be byte-identical, not merely behaviorally equivalent
   * (an explicit `1` left in the map after undo would read the same via `?? 1` but
   * is NOT the same state a replay from scratch would produce).
   */
  prevAbsent?: true;
}

/**
 * A **grounded** step: delete a combatant's named resource key entirely, restoring
 * TRUE absence. Emitted as the inverse of a {@link ResourceSetStep} whose
 * `prevAbsent` is set (the key genuinely didn't exist before that step), so undoing
 * "fire consumed Loading from a pristine weapon" reproduces a state byte-identical to
 * a fresh run rather than leaving an explicit residue value. The pilot `Reload` is a
 * second, DIRECT emitter — reusing this same primitive to restore a fired weapon's
 * Loading key to true absence (`reload.ts`'s `reloadSteps`), rather than
 * `stabilize.ts`'s mech-Stabilize `resource_set val:1` shape (which leaves an explicit
 * residue — fine there, since Stabilize never cares about byte-identity to a pristine
 * weapon). `restoreVal` is what to write back if this clear is itself undone (redo of
 * the original set, or undo of a direct Reload) — see `invertStep`.
 */
export type ResourceClearStep = {
  op: "resource_clear";
  target: CombatantId;
  /** The resource name (free string — the key into `current.resources`). */
  key: string;
  /** The value to restore if this clear is itself undone. */
  restoreVal: number;
}

/**
 * A **grounded** step: change a combatant's named
 * **resource** by `delta`. **Self-inverting** (negate `delta`), exactly like
 * {@link HpDeltaStep}/{@link HeatDeltaStep} — the natural shape for a consume
 * (`-1`) or a refund (`+1`). `applyResolvedStep` reads an absent resource as `0`
 * before applying the delta (so a first consume from an unset resource lands at
 * `delta`), and **floors the result at 0** (a resource never goes negative — a
 * consume past empty clamps). As with hp/heat, when the floor bites the resolver
 * is responsible for recording the **effective** delta so the inverse round-trips;
 * a resource that started at/above `|delta|` round-trips exactly.
 */
export type ResourceDeltaStep = {
  op: "resource_delta";
  target: CombatantId;
  /** The resource name (free string — the key into `current.resources`). */
  key: string;
  /** Signed change to the resource (negative = consume). */
  delta: number;
}

/**
 * A **record-only** step: the witness of a forced **save**.
 * Like {@link ToHitStep} it mutates no state — it records that a save was rolled
 * and its outcome; the save's *branch effects* (a failed Hull save adding Impaired)
 * ride as **separate grounded steps** in the same list (`status_add`, `hp_delta`,
 * …). So `save` inverts to nothing (record-only), and undo of the whole resolution
 * reverses the branch's grounded steps. The `target` of the result is whoever the
 * save's `stat` belongs to and `actor` is who rolled it; in Lancer these are the
 * same combatant (the one forced to save), recorded explicitly for the play-by-play.
 *
 * `against` is the save's stat (`"hull" | "agility" | "systems" | "engineering"` — a
 * free string), `target` is the save DC, `d20` the raw face, `total` the rolled total,
 * `result` the pass/fail. Spatial/area saves (a blast forcing many saves) record one
 * `save` step each — fine-grained record, coarse render.
 */
export type SaveStep = {
  op: "save";
  /** The combatant who rolled the save. */
  actor: CombatantId;
  /** The save stat rolled against (`"hull"`, `"agility"`, … — a free string). */
  against: string;
  /** The raw d20 face (`0` when {@link conceded} — no die is drawn). */
  d20: number;
  /** The signed accuracy/difficulty contribution to the save (`0` if none). */
  accuracy: number;
  /**
   * The per-source breakdown behind this save's accuracy/difficulty — one
   * {@link SaveContribution} per
   * `BoundModifier` whose `saveCheckBonus` facet actually folded into the count
   * this save rolled with. The save axis of the same provenance
   * {@link ToHitStep.accuracyContributions} gives the attack axis: the bare
   * `accuracy` number can tell a player *that* something changed their save, never
   * *which* thing — which is how a conferred Bondmate boon, a cause-scoped Stable
   * Structure +1, and an Ace flight bonus were all indistinguishable from a stat
   * line.
   *
   * Three properties, each load-bearing:
   *
   * - **Counts, not magnitude.** Entries carry the accuracy/difficulty *count*
   *   (`+1`, `-1`, `+2`) — the same units `ModifierContribution.saveCheckBonus`
   *   speaks. The sibling {@link accuracy} field is the **rolled d6 magnitude**
   *   those counts produced. Summing this list does NOT reproduce `accuracy`, and
   *   is not meant to; it reproduces the count handed to the roll. (Exactly the
   *   to-hit relationship, where contributions sum to `statusAccuracy`'s counts,
   *   not to `accuracy`.) No aggregate-count field is recorded beside it, because
   *   unlike to-hit — where intent and cover also feed the net — a save's net count
   *   *is* the fold's `saveCheckBonus`, so these entries are that total by
   *   construction.
   * - **Carrier-self, so no `side`.** See {@link SaveContribution}.
   * - **Omitted entirely when empty**, never `[]` — so an unmodified save's
   *   recorded step stays stable and no golden or replay fixture churns.
   *
   * Present only on a **rolled** save. A {@link conceded} or {@link autoFailed}
   * save draws no die and folds no accuracy into one, so there is nothing to
   * attribute; on a `save_ends` resolution the boon rides only the first re-save
   * (the "next save"), and the contributions ride exactly where the count does.
   */
  accuracyContributions?: SaveContribution[];
  /** The flat save bonus (the combatant's relevant save value); `0` if none. */
  bonus: number;
  /** `d20 + bonus + accuracy`. */
  total: number;
  /** The save DC the total was checked against. */
  dc: number;
  result: "pass" | "fail";
  /**
   * Whether `actor` **voluntarily failed** this save (RAW Core p.13: "a character may
   * always choose to fail a check or save") rather than rolling and losing.
   * `true` only for a recorded concession (no die drawn,
   * `d20` reads `0`, `result` is always `"fail"`); absent/`false`/`undefined` for an
   * ordinary rolled save (the default).
   */
  conceded?: boolean;
  /**
   * Whether `actor` **auto-failed** this save because it is Stunned and `against` names
   * a stat the carrier auto-fails (RAW Core p.78:
   * "automatically fail all HULL and AGILITY checks and saves") — a **recorded
   * skip-roll**, distinct from {@link conceded} (an involuntary state fact, not a player
   * choice): no die drawn, `d20` reads `0`, `result` is always `"fail"`. Absent/`false`
   * for an ordinary rolled or conceded save.
   */
  autoFailed?: boolean;
  /**
   * The `save_ends` status this save tries to end (`"Stunned"`), recorded only by the turn-start
   * re-save (`resolveSaveEnds`, `saves.ts`). A pass also records the `status_remove`, but a fail
   * records nothing else, so without this field the record could not say what was rolled against
   * (`narration-ledger-retirement`, the tick riders). Absent on every other save.
   */
  ends?: string;
  /**
   * **The area action that forced this save, as the map draws it** (`attack-animations` Phase 1): a
   * non-weapon attack such as a Frag Grenade rolls saves, not a to-hit, so its {@link SaveFx} rides
   * each save it forces. Presentation-only, like {@link ToHitStep.fx}. Absent on every other save.
   */
  fx?: SaveFx;
}

/**
 * A **grounded** step (RAW Core p.281 — Recharge): flip a
 * frozen weapon's OR granted action's **discharged** flag on its wielder. Modeled as a
 * boolean marker, exactly like {@link DestroyStep} — self-inverting (`discharged` flips) —
 * rather than folded into the generic `resources` bag: Recharge is a binary "fired, awaiting
 * a stochastic d6 clearance" gate, not a count, and it rides its own field on the
 * per-affordance ledger rather than that ledger's `uses` count. `key` is either the weapon's
 * **instance key** ({@link import("./types").weaponInstanceKey}) or a granted action's
 * affordance key ({@link import("./model/affordance").affordanceKey}) — the two id shapes
 * never collide, so two mounts of the same
 * weapon type AND a weapon/action pair on the same NPC all track independently. Emitted
 * `discharged: true` when a `tg_recharge`-tagged weapon fires (the attack path) or a
 * `tg_recharge`-tagged granted action activates (`resolveActivate`); emitted `discharged:
 * false` (the inverse) by `tickRecharge`'s start-of-turn roll for every discharged item
 * whose target ≤ the rolled face — one shared d6 draw per NPC turn (RAW: "only one roll is
 * required... even with multiple Recharge systems"), covering weapons and actions alike.
 *
 * **One ledger** (`affordance-table` Phase 3.1): `applyResolved` writes
 * `current.affordanceState[key].discharged` and nothing else. `key` is already the class-resolved
 * affordance key (`resolveAffordance` for a granted action) or the weapon's instance key, so the
 * write needs no second derivation, and the reducer's idempotence guard reads the SAME table every
 * emit-side guard does — a guard on one substrate against a reducer short-circuiting on another
 * pushes a step the reducer no-ops, whose inverse then clears a standing discharge.
 */
/**
 * A **grounded** step: set one affordance's (or weapon instance's) **consumed-use count** on
 * {@link import("./model/combatant").CombatantCurrent.affordanceState} — the per-affordance
 * state ledger that `decisions/0176` makes the single replacement for the `scene_use:` /
 * `limited:` prefixes of `resources`.
 *
 * **This is the only write** (`affordance-table` Phase 3.1). Through Phases 1-2 `spend_action_use`'s
 * scene branch and `limitedConsumeStep` each emitted this step *alongside* a `resource_set` writing
 * the `scene_use:<id>` / `limited:<key>` slot; Phase 3.1 deleted the prefixes, the `resource_set`
 * halves and every reader of them, so each site now emits ONE step and every consumed-use reader
 * takes `affordanceState`.
 *
 * `key` is the affordance's {@link import("./model/affordance").Affordance.key} — or, when the
 * frozen cap carries a `pool`, that shared pool key (`pool ?? affordance.key`, the same choice the
 * deleted `scene_use:` slot made), so one item minting several affordances off one charge pool
 * drains ONE counter.
 *
 * **`uses`/`prev` use presence, not a sentinel value**: `undefined` means "no count at all"
 * (the entry's `uses` field absent, and the entry itself dropped when nothing else is on it),
 * which is what makes the step self-inverting into genuine absence rather than an explicit `0`
 * residue — the same sparse-absent discipline {@link ResourceSetStep.prevAbsent} enforces for
 * `resources`. A fresh run and an undone run must be identical, not merely equivalent.
 */
export type AffordanceUseSetStep = {
  op: "affordance_use_set";
  target: CombatantId;
  /** The affordance key ({@link import("./model/affordance").affordanceKey}) or shared `pool` key. */
  key: string;
  /** The new consumed count; **absent** ⇒ clear the count back to genuine absence. */
  uses?: number;
  /** The count before this step, for the inverse; **absent** ⇒ there was none. */
  prev?: number;
}

export type DischargeSetStep = {
  op: "discharge_set";
  target: CombatantId;
  /**
   * The discharged item's key — a weapon's instance key ({@link
   * import("./types").weaponInstanceKey}) or a granted action's affordance key ({@link
   * import("./model/affordance").affordanceKey}), already class-resolved by `resolveAffordance`
   * at the emit site. Both index the one `current.affordanceState` ledger.
   */
  key: string;
  /** `true` = mark discharged (just fired); `false` = clear it (recharged — the inverse). */
  discharged: boolean;
}

/**
 * A **record-only** step: the witness of a **two-roll opposed/contested check** — the
 * resolution model beyond one-roll-vs-a-number (`to_hit`/`save`). Like {@link SaveStep} it mutates no
 * state; it records both sides' draws and the winner. The consequences (a Search
 * reveal, a Grapple break/tiebreak) ride as **separate grounded steps** the consumer
 * appends alongside this one — so `contest` inverts to nothing, and undo of the whole
 * resolution reverses only those grounded consequence steps.
 *
 * `challenger` is the initiator (the one who called the contest — Search's searcher,
 * Grapple's grappler); `defender` is the target of the contest. Each side's
 * `*Axis` is a **label only**, for the play-by-play (`"systems"`/`"agility"`/`"hull"`
 * for a HASE-stat contest, but not restricted to one — Stasis Bolt's Interdiction
 * Point contests a *ranged attack roll*, not a HASE stat) — the primitive never reads
 * `base[axis]` itself; the consumer resolves each side's bonus and passes it in.
 * `winner`/`tie` are the **RAW initiator-wins-ties** outcome ("If the result is a tie,
 * the attacker — the one who initiated the contest — wins" — no per-consumer tie
 * parameter).
 */
export type ContestStep = {
  op: "contest";
  /** The initiator — the combatant who called the contest. Wins ties. */
  challenger: CombatantId;
  /** The target of the contest. */
  defender: CombatantId;
  /** Label only, for the play-by-play (e.g. `"systems"`) — not a `base[axis]` read. */
  challengerAxis: string;
  /** Label only, for the play-by-play (e.g. `"agility"`) — not a `base[axis]` read. */
  defenderAxis: string;
  /** The challenger's draw: `d20 + bonus + accuracy = total`. */
  challengerRoll: { d20: number; accuracy: number; bonus: number; total: number };
  /** The defender's draw: `d20 + bonus + accuracy = total`. */
  defenderRoll: { d20: number; accuracy: number; bonus: number; total: number };
  /** The RAW outcome (initiator wins ties — `tie: true` implies `winner: "challenger"`). */
  winner: "challenger" | "defender";
  tie: boolean;
  /**
   * Whether the challenger/defender **auto-failed** this contest because they are
   * Stunned and the matching axis names a stat they auto-fail (RAW Core p.78 — e.g. a
   * Grapple-break HULL contest against a
   * Stunned party) — a recorded skip-roll: that side's `*Roll.d20` reads `0` and its
   * `total` is not what decided the outcome (the auto-fail forces a loss regardless of
   * the other side's total, unless BOTH sides auto-fail, in which case the ordinary
   * tie-goes-to-the-challenger rule decides — RAW doesn't cover a doubly-auto-failed
   * contest). Absent/`false` for an ordinary rolled side.
   */
  challengerAutoFailed?: boolean;
  defenderAutoFailed?: boolean;
}

/**
 * A **grounded** step: record a combatant moving from one cell to another. The
 * spatial *answer* (where the unit ends up, whether the path was legal) is **recorded
 * input**, not a map query — `resolve` records the `from`/`to` it was given, because
 * the manual move tier already validated reachability via `move_token`.
 * Self-inverts by swapping `from`/`to`. `applyResolvedStep` writes `to` onto the
 * combatant's `pos` (the same field `move_token` writes); a combatant with no map
 * position (`pos == null`) still records the step but playback only sets `pos` when
 * the recorded move is the current one — kept total like every other step. `from`
 * is captured so the inverse restores the exact prior cell.
 *
 * It deliberately does **no** pathfinding/cost/occupancy validation — that lives in
 * `move_token`'s manual tier and the spatial answerer.
 */
export type MoveStep = {
  op: "move";
  target: CombatantId;
  /** The cell the combatant moved from (for the inverse); `null` if it was unplaced. */
  from: Cell | null;
  /** The cell the combatant moved to. */
  to: Cell | null;
}

/**
 * A **grounded** step: add `delta` cells to the acting unit's move budget
 * (`activation.turn.budget.move`) — the effects-atom seam {@link import("./commands").GrantMoveCommand}'s
 * doc reserved for the first authored entry that grants movement (Ace's Afterburners: "you may
 * move an additional 1d6 spaces"). The command is its GM-tier peer and keeps Boost's arithmetic;
 * this step is the same `move + cells` from a resolution. It leaves the flight heading alone, as
 * `grant_move` does.
 *
 * The budget only exists for the unit mid-turn, so playback is a no-op unless `target` is the
 * acting unit (the emitter checks the same and emits nothing otherwise). It self-inverts by
 * negating `delta`; it never clamps, so the inverse round-trips exactly.
 */
export type MoveBudgetDeltaStep = {
  op: "move_budget_delta";
  target: CombatantId;
  /** Cells added to the move budget (negative only as an inverse). */
  delta: number;
}

/**
 * A **grounded** step — record a combatant being **involuntarily displaced**
 * (Knockback, a forced push/pull). A positional primitive **distinct from voluntary
 * {@link MoveStep}** in both authoring intent (an effect forces it; the unit did not
 * spend movement) and provenance (the displacement *distance* is the tag's bound
 * `{VAL}`; the destination *cell* is a spatial-answerer concern — `resolve` queries
 * `@lancer-vtt/space` for the push direction/legality and records the resolved `to`
 * here, exactly as it records a die or a recorded boolean). Keeping it a separate op
 * (not a `move` with a flag) keeps the play-by-play honest ("knocked back 2") and the
 * undo trivially correct.
 *
 * Like {@link MoveStep} it self-inverts by swapping `from`/`to`, and `applyResolvedStep`
 * folds it rules-free (writes `to` onto `pos`) — the map *query* happened once at
 * resolve time and is baked in; playback never re-queries the map. `from` is
 * captured for the exact inverse; a `to == null` (the answerer couldn't place it —
 * no map, off-map, or an unanswerable arc) records the displacement as a no-op move
 * honestly rather than faking a destination (the distance still rides the recorded
 * tag rider / the warning surfaces it).
 */
export type ForcedMoveStep = {
  op: "forced_move";
  target: CombatantId;
  /** The cell the combatant was displaced from (for the inverse); `null` if unplaced. */
  from: Cell | null;
  /** The cell the displacement landed on; `null` when the answerer could not place it. */
  to: Cell | null;
  /**
   * The displacement distance in spaces, recorded for the play-by-play. **Two different
   * conventions ride this one field, by which atom emitted the step**: an INVOLUNTARY
   * push/pull (`interpretForcedMove` — Knockback, a
   * chosen-direction shove) records the tag's authored bound `{VAL}` even when the push
   * stopped short or couldn't be placed at all — the intended force is what the play-by-play
   * cares about ("knocked back 2"), and a shortfall surfaces separately as a warning. A
   * VOLUNTARY self-reposition (`interpretMoveSelfTowardTarget` — Hunter's Lunge, Ace's
   * Acrobatics) instead records the distance actually TRAVELLED (`from` → `to`, `0` when
   * either is `null`) — a chosen 1-space answer against an authored "up to 4," or a
   * wall-blocked walk that made zero progress, must not log the authored ceiling as if it
   * had been reached.
   */
  distance: number;
  /**
   * Why a push stopped short, when it did: the space it was driven into (`at`), the characters
   * occupying it, and whether an obstruction stopped it (a blocking character, object or solid
   * terrain) rather than an occupant it could pass but not end on. Record-only, written by an
   * `away_from` push (`interpretForcedMove`); Juggernaut's Kinetic Mass Transfer reads it.
   * Absent for a push that went its full distance, stopped at the map edge, or wasn't a push.
   */
  collision?: ForcedMoveCollision;
  /**
   * The unit teleported (RAW Core p.63): it left `from` and appeared at `to` without crossing the
   * spaces between, so the board blinks it rather than sliding it. Written by the `teleport` atom.
   */
  teleport?: true;
}

/** The {@link ForcedMoveStep.collision} record. */
export type ForcedMoveCollision = {
  at: Cell;
  occupants: CombatantId[];
  obstructed: boolean;
};

/**
 * A **record-only** step (RAW Core p. 77 — Invisible's
 * pre-roll 50% miss chance): the witness of the ONE recorded coin drawn immediately before
 * the d20 (`resolve/attackPreRoll.ts`, a stage of `resolveSingleTarget`), on the losing (miss) face. Like {@link
 * ToHitStep}/{@link SaveStep} it mutates no state and inverts to nothing — it witnesses that
 * the attack missed outright *before* a to-hit roll ever happened, so no `ToHitStep` is
 * emitted for this resolution at all (a genuinely different outcome than a rolled miss,
 * which still emits a `to_hit` step with `result: "miss"`). `coin` is the drawn face (`1` —
 * always `1` when this step is emitted, the early-out only fires on the losing face);
 * `chance` is the facet's configured probability (`0.5` for Invisible), carried for the
 * play-by-play/audit trail.
 */
export type MissChanceStep = {
  op: "miss_chance";
  attacker: CombatantId;
  target: CombatantId;
  /** The recorded coin draw (a d2: `1` = miss face, `2` = proceed face). Always `1` here. */
  coin: number;
  /** The configured miss probability that produced this coin (e.g. `0.5`). */
  chance: number;
}

/**
 * A **record-only** step: a cost a resolution owed and a replacement waived (`decisions/0211`,
 * `resolution-events` 2.4). Settlement (`packages/rules`' `events/gameEvents.ts`) grounds a charge
 * spend as an {@link AffordanceUseSetStep}; when a `replacement` clause prevents it (Ammo Case III's
 * Efficiency on a first-roll crit), it records this instead, so the receipt can say what was not
 * paid and why. Mutates nothing and inverts to nothing.
 *
 * `by` names the replacing clause: the combatant that carries it, the entry id and the clause's
 * authored name. It is redacted like any other recorded source (`resolutionRedaction.ts`): a
 * class-tier carrier's undisclosed entry comes out as the sentinel, with no id.
 */
export type CostWaivedStep = {
  op: "cost_waived";
  /** Whose cost it was. */
  target: CombatantId;
  /** The pool's ledger slot the cost would have spent from. */
  key: string;
  /** How many charges were not spent. */
  amount: number;
  by: { carrier: CombatantId; id?: string; name: string };
}

/**
 * A **grounded** step: set a mine's {@link
 * import("./types").MineProfile.armed} flag to an absolute value. Mirrors {@link
 * BurnSetStep}'s absolute-with-`prev` shape. Emitted by the turn-end arm sweep (`armed:
 * true`, deployer's turn just ended); `prev` is always `false` in practice, since a mine
 * only ever arms once (disarm removes the mine outright rather than flipping
 * this back). `target` must name a combatant carrying a `mineProfile`; an unknown target
 * or one with no `mineProfile` is a silent no-op at playback (replay is total, mirrors
 * every other step).
 */
export type MineArmedSetStep = {
  op: "mine_armed_set";
  target: CombatantId;
  /** `true` = armed (live); `false` = the inverse (never armed / unarmed). */
  armed: boolean;
  /** The value before this step (for the inverse). */
  prev: boolean;
}

/**
 * A **grounded** step: set a combatant's top-level
 * {@link import("./types").Combatant.concealed} flag to an absolute value, mirroring
 * {@link BurnSetStep}'s absolute-with-`prev` shape. **Distinct from the manual-tier
 * `set_concealed` COMMAND** (`commands.ts`, GM-only fiat) — this is the step-layer
 * primitive a *resolution* emits for a gameplay reveal (a mine's detonation; a
 * detection/disarm success rides the same op), a recorded, undoable, replayable
 * consequence of the dice, never GM fiat. See `Combatant.concealed`'s doc for the
 * two-tier posture.
 */
export type ConcealedSetStep = {
  op: "concealed_set";
  target: CombatantId;
  /** The new concealed value. */
  concealed: boolean;
  /** The value before this step (for the inverse). */
  prev: boolean;
}

/**
 * A **grounded** step: set a
 * combatant's top-level {@link import("./types").Combatant.disclosure} to an absolute
 * value, mirroring {@link ConcealedSetStep}'s absolute-with-`prev` shape (generalized
 * from a boolean to the 3-state-plus-unset {@link DisclosureLevel}). Emitted by a
 * successful **Scan**'s reveal atom (the interpreter reads the target's prior level;
 * the applier below writes it via the SAME `withDisclosureLevel` mutation the GM's
 * manual `set_disclosure` reducer case calls — one field, two writers, neither
 * duplicates the mutation). `disclosure`/`prev` are each `DisclosureLevel | undefined`
 * (undefined = "never set") so undo can restore the genuine "never scanned" state, not
 * just cycle between two named levels.
 */
export type DisclosureSetStep = {
  op: "disclosure_set";
  target: CombatantId;
  /** The new disclosure level (or `undefined` to clear it). */
  disclosure: DisclosureLevel | undefined;
  /** The value before this step (for the inverse). */
  prev: DisclosureLevel | undefined;
}

/**
 * A **grounded** step: set a combatant's top-level
 * {@link import("./types").Combatant.revealedFeatureIds} to an absolute value (`decisions/0207`,
 * a masked NPC's features reveal per unit as a resolution names them). Absolute-with-`prev`, like
 * {@link DisclosureSetStep}, so the inverse is a swap and undo restores the exact prior set.
 * `ids` is the WHOLE new set, not the additions; `undefined` on either side means "none revealed"
 * (the field is sparse: an empty set is stored absent). Emitted by the encounter room after a
 * resolution applies, never by the referee's own plans.
 */
export type FeaturesRevealedStep = {
  op: "features_revealed";
  target: CombatantId;
  /** The new revealed set (or `undefined` to clear it). */
  ids: string[] | undefined;
  /** The set before this step (for the inverse). */
  prev: string[] | undefined;
}

/**
 * A **grounded** step (the `deploy_object_from_content` atom):
 * splice a freshly-minted object/deployable {@link Combatant} into
 * `state.combatants` (the ONE place a resolved step list creates a brand-new
 * combatant — every other grounded step mutates an EXISTING one). `combatant` is the
 * full record — including its already-resolved `pos` — baked in at resolve time by
 * the interpreter's spatial-answerer placement query (playback never re-queries
 * the map, mirroring {@link ForcedMoveStep}'s own "resolved once, recorded, replayed
 * rules-free" discipline). **Idempotent at playback**: if a combatant with this id is
 * ALREADY present (a re-run, or a same-`groupId` redeploy the interpreter chose not
 * to no-op earlier), `applyResolvedStep` leaves state untouched rather than
 * clobbering it — mirrors {@link StatusAddStep}'s own idempotent-by-presence
 * discipline.
 */
export type SpawnObjectStep = {
  op: "spawn_object";
  /** The freshly minted combatant, `pos` already resolved (or `null` — an honest no-placement record). */
  combatant: Combatant;
}

/**
 * The inverse of {@link SpawnObjectStep}: remove the named combatant from
 * `state.combatants` (undoing a deploy). `combatant` carries the FULL record it
 * removes — not just the id — so a redo (the inverse-of-inverse, `invertStep` turning
 * this back into a `spawn_object`) recreates the exact original record, mirroring
 * {@link StatusRemoveStep} carrying every facet needed to rebuild the exact
 * `StatusAddStep` on redo. Idempotent: removing an already-absent id is a no-op.
 */
export type DespawnObjectStep = {
  op: "despawn_object";
  /** The combatant being removed (its `id` is the removal key). */
  combatant: Combatant;
}

/**
 * A **grounded** step: splice a freshly-created
 * {@link AreaEffect} into `state.areaEffects` — the exact structural mirror of
 * {@link SpawnObjectStep}, one tier down (a standing area record rather than a
 * combatant). `area` carries the full record. **Idempotent at playback**: an
 * already-present id is left untouched rather than clobbered, mirroring
 * `spawn_object`'s own idempotent-by-presence discipline.
 */
export type AreaAddStep = {
  op: "area_add";
  /** The freshly created area effect. */
  area: AreaEffect;
}

/**
 * The inverse of {@link AreaAddStep}: remove the named area from
 * `state.areaEffects` (undoing a create, or applying dispersal). `area` carries the
 * FULL record it removes — not just the id — so a redo (the inverse-of-inverse)
 * recreates the exact original record, mirroring {@link DespawnObjectStep}.
 * **Idempotent**: removing an already-absent id is a silent no-op.
 */
export type AreaRemoveStep = {
  op: "area_remove";
  /** The area effect being removed (its `id` is the removal key). */
  area: AreaEffect;
}

/** Every resolved step variant. Deliberately open — a new resolved consequence joins as a sibling. */
export type ResolvedStep =
  | ToHitStep
  | DamageStep
  | HpDeltaStep
  | HeatDeltaStep
  | BurnSetStep
  | StatusAddStep
  | StatusRemoveStep
  | StatusSuspendSetStep
  | ResourceSetStep
  | ResourceClearStep
  | ResourceDeltaStep
  | SaveStep
  | MoveStep
  | MoveBudgetDeltaStep
  | ForcedMoveStep
  | StructureDeltaStep
  | StressDeltaStep
  | InterceptStep
  | TableCheckStep
  | DestroyStep
  | DestroyItemStep
  | ContestStep
  | DischargeSetStep
  | AffordanceUseSetStep
  | MissChanceStep
  | CostWaivedStep
  | MineArmedSetStep
  | ConcealedSetStep
  | DisclosureSetStep
  | FeaturesRevealedStep
  | SpawnObjectStep
  | DespawnObjectStep
  | AiCascadeCheckStep
  | LinkStep
  | AreaAddStep
  | AreaRemoveStep;

/**
 * The **breadcrumb** that accompanies a resolution's `ResolvedStep[]` — not a
 * step itself (it isn't folded by `applyResolved`, has no inverse, and doesn't
 * ride the array), but recorded alongside it for exactly one reason: the
 * narrator (`resolution-narration`) renders off the durable feed with no
 * encounter state loaded and no compendium lookup, so anything its prose needs
 * to say — who acted, with what, at whom — has to be recorded here or it can't
 * be said at all. `ResolvedStep[]` already records *what happened*; `about`
 * records *who meant to do it*, the one fact the steps alone don't carry (a
 * `to_hit`/`damage` step knows the attacker and target of the ROLL, but not the
 * weapon's display name, the action verb, or the full declared target list).
 *
 * Additive and optional throughout — old `feed.jsonl` entries predate this
 * field entirely, so every consumer (the narrator included) must degrade
 * cleanly when it, or any field on it, is absent, never crash or print
 * `undefined`.
 */
export type ResolutionAbout = {
  /**
   * The acting COMBATANT id — deliberately not the acting SLOT id
   * (`resolution.actorId`, which already names the slot elsewhere on the
   * broadcast rider): the story line needs "who", and a combatant id is what
   * every other step and the `names` map already key by.
   *
   * Every rider builder sets it (`narration-ledger-retirement` 2.1). Who "acted" for a
   * resolution nobody declared: a mine's detonation is its deployer's; a trigger's warning rider
   * is its carrier's. GM damage has no acting combatant and records {@link GM_ACTOR}.
   */
  actor: CombatantId;
  /**
   * The action verb, lowercase, exactly as the intent named it (`"skirmish"`,
   * `"barrage"`, `"improvised"`, …). A head-less rider (no attack roll) records its
   * resolution kind here (`"grapple_break"`, `"deploy_mine"`, `"gm_damage"`), because the CLI's
   * narrator receives `about` but not the rider's `kind` (see {@link barrage}). Absent for a
   * reaction, and on entries persisted before the field existed.
   */
  action?: string;
  /**
   * **The item that fired, when it is not a weapon** (`narration-ledger-retirement` 2.2): the
   * system, pilot gear or NPC feature an activation used (`"Frag Grenades"`, id
   * `pg_frag_grenades`), or the drone or mine a deploy/mine resolution minted or set off (its
   * combatant name; no `id`). The activation's name is frozen onto its `Affordance` at spawn,
   * since the engine holds no compendium.
   *
   * **Class-tier redaction: the same predicate as `weapon`, with the steps' sentinel.** A
   * class-tier hostile's item keeps its place with `name` replaced by
   * {@link UNDISCLOSED_FEATURE_NAME} and `id` dropped (`redactAboutItem`,
   * `resolutionRedaction.ts`), so the story line still says something was used.
   */
  item?: { name: string; id?: string };
  /**
   * The weapon or system that fired, if any. Optional even when one did fire —
   * see the disclosure rule at `EncounterRoom.broadcastResolution`
   * (`backend/src/encounterRoomCore.ts`), which strips this field for a
   * hostile NPC whose weapons a viewer's dossier would redact: the record is
   * viewer-agnostic, so that redaction happens once, here, before either
   * broadcast sink, rather than at render time per viewer.
   *
   * **This field is only half the seam.** The same broadcast runs
   * {@link import("./resolutionRedaction").redactUndisclosedSteps} immediately after,
   * behind the SAME disclosure predicate, over the recorded STEPS — which named the
   * loadout independently through `ToHitStep.accuracyContributions[].name` and
   * `DamageStep.source`. Dropping this field alone left the receipt printing what the
   * breadcrumb had just hidden.
   */
  weapon?: {
    name: string;
    id?: string;
    /**
     * The fired weapon's **area template** — its Lancer shape word + size in spaces
     * (`resolution-narration` 2.1), recorded off the same frozen `WeaponProfile.range`
     * `resolveAreaAttack` fans the shot out over (`areaShapeOf`,
     * `packages/rules/src/encounter/resolve/area.ts`). Absent for an ordinary
     * single-target weapon, and absent on every entry persisted before this field
     * existed.
     *
     * **Recorded rather than re-derived, for the same reason `name`/`id` are.** The
     * narrator names the shape in an area attack's story line ("fires the Mortar
     * (Blast 1)"), and it renders off the durable `feed.jsonl` with no encounter
     * loaded and no compendium in reach — a render-time `areaShapeOf` lookup would
     * need the weapon's `range`, which nothing on the record carries. `size` is the
     * template's radius/length in spaces, exactly as `areaShapeOf` returns it (it can
     * legitimately be `0`; a renderer should print the bare shape word then).
     */
    area?: { kind: "blast" | "burst" | "line" | "cone"; size: number };
    /**
     * **This shot was a MELEE attack** (`comms-attack-reaction-cards` 1.2) — recorded off the
     * same `isRangedWeaponAttack` read the resolver's own ranged/melee gates make, so an
     * either-mode weapon (a Ram Cannon) records the mode THIS attack resolved in, not the
     * weapon's shape in general. A rangeless profile counts as melee, exactly as
     * `isRangedAttack` treats it (the fallback unarmed strike, and the built-in Ram /
     * Grapple / improvised-attack profiles, which are threat-1). Absent for a ranged shot and on
     * every entry persisted before this field existed.
     *
     * **Recorded rather than re-derived, for the same reason `area` is**: the narrator's verb
     * ("attacks X with the Y" for melee, "fires the Y at X" otherwise) renders off the durable
     * feed with no weapon range in reach.
     */
    melee?: true;
  };
  /**
   * **The tech OPTION a tech attack fired** (`comms-attack-reaction-cards` 1.3), so a card can
   * read `Invade · Fragment Signal`. Recorded by `refereeIntent.ts` only when the record gives
   * a real id and name: the answered `choose` of the tech action's own overlay entry (Invade's
   * `invade_option` menu, id `fragment_signal`, name taken from the option's authored label).
   * Absent on a miss (the option never took effect), on a system-granted tech attack
   * (`ms_markerlight:0`, whose action name is not on anything the compendium-free engine
   * holds), and on every entry persisted before this field existed.
   *
   * `action` is the Quick Tech menu's RAW name for the action that fired (`"Invade"`,
   * `packages/rules/src/encounter/quickTech.ts`), when the menu routes it.
   *
   * **Class-tier redaction: the same predicate as `weapon`, with the steps' sentinel instead of
   * a drop.** A class-tier hostile's option keeps its place with `name` replaced by
   * {@link UNDISCLOSED_FEATURE_NAME} and the raw `id` dropped (`redactTechOption`,
   * `resolutionRedaction.ts`), so a card still says a tech option fired. `action` stays: it names
   * a core action every character has, not the hostile's loadout.
   */
  techOption?: { id?: string; name: string; action?: string };
  /**
   * The DECLARED target ids, as named on the intent — NOT the resolved/actual
   * targets (an area attack's declared target may differ from who the shape
   * ultimately caught; that's `ResolvedStep`'s job to record per-target).
   * Optional.
   */
  targets?: CombatantId[];
  /**
   * **This resolution is ONE shot of a Barrage** (`resolution-narration` 2.2) — recorded off
   * the resolving command's own `kind` field (`ApplyResolvedCommand`, set to `"barrage"` by
   * `refereeIntent.ts`'s `request_barrage` branch).
   *
   * **Recorded here even though the broadcast rider already carries a `kind` string**, for two
   * measured reasons. (1) `kind` does not reach every narrator surface: the CLI's
   * `EncounterResult` (`cli/src/encounter-session.ts`) captures `about` off the first
   * `encounter_change` but never `kind`, so `about` is the only channel that reaches BOTH the
   * browser card and the CLI. (2) The narrator's governing rule is that the record is its only
   * input — a fact its prose says out loud belongs on the record it reads, not on a coarse
   * optional routing string a caller happens to pass through.
   *
   * **What it is NOT: a per-chain bundle.** Measured off a real three-shot Barrage's
   * `feed.jsonl` (2026-09-06): since `barrage-sequential-resolution` (`decisions/0049`) every
   * Barrage shot is its own independently-committed resolution with its own `apply_resolved`,
   * its own feed entry, and exactly ONE `to_hit`. Two shots never share a step list. So this
   * flag says "this shot was part of a Barrage" and nothing about how many shots the chain
   * fired or which one this is — neither fact is on the record, and the shot ordinal is not
   * derivable from the `attack_ordinal_this_turn` `resource_delta` (a `delta: 1` tick, not a
   * value). {@link shot} is the field that carries the ordinal now.
   */
  barrage?: true;
  /**
   * **Which shot of a multi-shot action this resolution was** (`comms-attack-reaction-cards`
   * 1.1): `action` is the `AttackLock` it belongs to, `index` its 1-based position, read off the
   * lock's fired-weapon list at the moment the shot resolved (`refereeIntent.ts`). Present on
   * every Barrage shot, and on a Skirmish only when that Skirmish opened a lock (its mount
   * carries an eligible Auxiliary rider, index 1) or IS the rider (index 2). A Skirmish that can
   * have no rider opens no lock and records nothing.
   *
   * **Why it exists: the frontend groups a multi-shot action's cards, and adjacency is not a
   * safe key.** The feed is append-only (`decisions/0040`), so shot 2 can never be folded into
   * shot 1's entry; grouping is rendering over entries. But another actor's Brace or Overwatch
   * resolves BETWEEN shots (`AttackLock`'s doc comment) and its durable outcome line lands
   * between the two cards, so "consecutive entries by the same actor" splits or mis-joins a
   * group. `index: 1` opens a group; a later entry with the same actor and `action` continues
   * it. Old entries without this field render alone.
   */
  shot?: { action: "barrage" | "skirmish"; index: number };
  /**
   * **This shot is an AUXILIARY RIDER and deals no bonus damage** (Core p.70 for a Skirmish's
   * Aux rider, p.71 for a Barrage's) — the display twin of `ApplyResolvedCommand.noBonus`,
   * carried onto the breadcrumb by `refereeIntent.ts` so the narrator can say it
   * (`resolution-narration` 2.2). Independent of {@link barrage}: a Skirmish Aux rider carries
   * this with no `barrage` flag at all, and a Barrage's non-rider shots carry `barrage` without
   * this.
   *
   * **Still an inert marker on the ENGINE side** — see `ApplyResolvedCommand.noBonus`'s own doc
   * comment: nothing suppresses anything off it, because no bonus-damage suppression engine
   * exists yet. Mirroring it here does not change that; it only makes the already-recorded fact
   * visible to a reader instead of unit-test-only (before 2.2 the wire did not surface it at
   * all — the bash original of `cli/proofs/skirmish-aux-rider.test.ts` recorded exactly that).
   */
  noBonus?: true;
  /**
   * **This Main's shot paired with an Aux its mount fired EARLIER this action, and that Aux was the
   * rider** (Core p.70/p.71: the AUXILIARY is the weapon that deals no bonus damage). The rider is
   * only known once the Main fires, and the Aux's own entry is already recorded (append-only,
   * `decisions/0040`), so the pairing rides the Main's resolution instead of a `noBonus` on the
   * Aux's (`decisions/0049`, 2026-09-25 amendment). `weapon` names the Aux; it is dropped with
   * `weapon` for a class-tier hostile, leaving `{}`. This Main itself is the primary.
   */
  auxRider?: { weapon?: { name: string; id?: string } };
  /**
   * **This attack was a reaction: Overwatch.** Stamped by `refereeIntent.ts` off the attack's own
   * cost (`reactionId: "overwatch"`), so the feed line and the narrator can say so. Without it a
   * taken Overwatch logged as a plain attack and players couldn't find it (game night
   * 2026-09-23). `about.action` stays absent for a reaction, as before. `"covering_fire"` is
   * Covering Fire's reaction shot (`t_heavy_gunner:1`), its own reaction, not an Overwatch. Any
   * other value is a shot a rule grants (`reactionShotGrant`: Impending Threat, Moving Target), the
   * feature id whose reaction it spent; `about.item` then names the feature, so the card and the
   * feed say "Impending Threat" and class-tier redaction already covers it.
   */
  reaction?: string;
  /**
   * **The GM forced this action past the rules** (`decisions/0209`): a `gm_force` resolution. Set
   * only by the override's referee door (`refereeForce.ts`), mirrored from the logged
   * `apply_resolved`'s own `forced`. COMMS shows a GM OVERRIDE chip for everyone.
   */
  forced?: true;
};

// ── inverse-at-the-step-layer ────────────────────────────────────────────────────

/**
 * Project a live {@link import("./types").StatusInstance}'s per-instance facets onto the
 * `status_add`/`status_remove` step that records it. Every emitter
 * that removes a status it already holds in hand goes through this — the `remove_status` /
 * `remove_status_from_source` atoms, Shut Down's tech-caused sweep, the Hidden-reveal
 * clears, the Flying-grounds co-effect — so a burned status can be rebuilt exactly when the
 * removal is undone.
 *
 * A hand-enumerated spread at each of those sites is what let `ruling` (and before it
 * `weapon` and `cause`) go missing at some of them and not others, silently.
 */
export function statusFacetsFromInstance(inst: StatusInstance) {
  return {
    expiry: inst.expiry,
    ...(inst.appliedTurn !== undefined ? { appliedTurn: inst.appliedTurn } : {}),
    ...(inst.source !== undefined ? { source: inst.source } : {}),
    ...(inst.partner !== undefined ? { partner: inst.partner } : {}),
    ...(inst.weapon !== undefined ? { weapon: inst.weapon } : {}),
    ...(inst.cause !== undefined ? { cause: inst.cause } : {}),
    ...(inst.ruling !== undefined ? { ruling: inst.ruling } : {}),
    ...(inst.endsOn !== undefined ? { endsOn: inst.endsOn } : {}),
  };
}

/**
 * The step→step half of the same projection: carry a `status_add`/`status_remove` step's
 * recorded facets onto its inverse, so a redo (invert-of-invert) rebuilds the exact original
 * step. Used by BOTH directions of {@link invertStep}, which is why it is one function —
 * the two directions had drifted from each other before.
 */
function statusFacetsFromStep(step: StatusAddStep | StatusRemoveStep) {
  return {
    ...(step.expiry !== undefined ? { expiry: step.expiry } : {}),
    ...(step.appliedTurn !== undefined ? { appliedTurn: step.appliedTurn } : {}),
    ...(step.source !== undefined ? { source: step.source } : {}),
    ...(step.partner !== undefined ? { partner: step.partner } : {}),
    ...(step.weapon !== undefined ? { weapon: step.weapon } : {}),
    ...(step.cause !== undefined ? { cause: step.cause } : {}),
    ...(step.ruling !== undefined ? { ruling: step.ruling } : {}),
    ...(step.endsOn !== undefined ? { endsOn: step.endsOn } : {}),
  };
}

/** Errors unless `T` is `never` — the carrier for the two exhaustiveness pins just below. */
type AssertNever<T extends never> = T;

/**
 * The `StatusInstance` facets the step layer deliberately does NOT carry, with the reason
 * each is safe to omit. Its only job is to make the pins below meaningful: without a declared
 * complement, "every facet is accounted for" would be vacuous.
 *
 * - `name` — not a *facet*: it is the step's own `status` field, the dedup key both directions
 *   are addressed by. It cannot go missing without the step being malformed.
 * - `suspended` — has its own dedicated step (`status_suspend_set`, absolute-with-`prev` and
 *   self-inverting), so it round-trips on its own rail. The one consequence, stated so nobody
 *   re-derives it: removing a *suspended* instance and undoing that removal restores it
 *   un-suspended. Pre-existing behaviour, not something this list introduced.
 */
type UncarriedStatusFacet = "name" | "suspended";

/**
 * **Compile-time exhaustiveness pins, on the INPUT side.** Every key
 * of {@link import("./types").StatusInstance} must come out of BOTH projections above or be
 * declared uncarried — so adding a facet to `StatusInstance` fails `bun run typecheck` rather
 * than silently vanishing on the next undo.
 *
 * Two properties, and both are load-bearing:
 *
 * 1. **Keyed on the SOURCE type**, not on the projections' output. An output-keyed pin only
 *    ever sees the keys the projection already names, so a new source field changes nothing
 *    about the output shape and the pin stays green — exactly the bug. Witnessed by widening
 *    `StatusInstance` with a throwaway field: `TS2344: Type '"pinWitness"' does not satisfy
 *    the constraint 'never'`.
 * 2. **Keyed through `ReturnType`**, so *deleting* a line from either projection also fails
 *    the build — a hand-maintained name list would not. Witnessed by deleting the `ruling`
 *    spread: `TS2344: Type '"ruling"' does not satisfy the constraint 'never'`.
 *
 * The concrete failure this exists to prevent: a GM grants "Called Shot" (+1 accuracy); the
 * attacker attacks; the compiled self-removal fires; the GM presses undo; the rebuild drops
 * the facet; the chip reappears with no mechanics, forever, with no type error and no crash.
 * The pins force the decision; the runtime round-trip in `./steps.test.ts` proves the
 * carry-through works.
 */
type _EveryStatusFacetCarriedFromInstance = AssertNever<
  Exclude<keyof StatusInstance, keyof ReturnType<typeof statusFacetsFromInstance> | UncarriedStatusFacet>
>;
type _EveryStatusFacetCarriedFromStep = AssertNever<
  Exclude<keyof StatusInstance, keyof ReturnType<typeof statusFacetsFromStep> | UncarriedStatusFacet>
>;

/**
 * The inverse of a single step. Record-only steps (`to_hit`, `damage`, `intercept`) witness a
 * roll and mutate nothing, so they invert to *nothing* (filtered out). Grounded
 * steps self-invert: `hp_delta` negates its delta. Returning `null` for a
 * record-only step lets the caller drop it from the inverse list.
 */
export function invertStep(step: ResolvedStep): ResolvedStep | null {
  switch (step.op) {
    case "hp_delta":
      return { op: "hp_delta", target: step.target, delta: -step.delta };
    case "heat_delta":
      return { op: "heat_delta", target: step.target, delta: -step.delta };
    case "structure_delta":
      return { op: "structure_delta", target: step.target, delta: -step.delta };
    case "stress_delta":
      return { op: "stress_delta", target: step.target, delta: -step.delta };
    case "destroy":
      // Carry `vaporized` through unchanged (not stripped) — see the field's doc: a
      // redo (invert-of-invert) must rebuild the exact original step, and `applyResolvedStep`'s
      // `destroyed: false` branch ignores it anyway (it clears both markers unconditionally).
      return {
        op: "destroy",
        target: step.target,
        destroyed: !step.destroyed,
        ...(step.vaporized !== undefined ? { vaporized: step.vaporized } : {}),
      };
    case "discharge_set":
      return { op: "discharge_set", target: step.target, key: step.key, discharged: !step.discharged };
    case "affordance_use_set":
      // Symmetric in `uses`/`prev`, both presence-carrying: an undo back to `undefined` restores
      // genuine absence (the entry is dropped), and inverting again reproduces this exact step.
      return {
        op: "affordance_use_set",
        target: step.target,
        key: step.key,
        ...(step.prev !== undefined ? { uses: step.prev } : {}),
        ...(step.uses !== undefined ? { prev: step.uses } : {}),
      };
    case "destroy_item":
      return { op: "destroy_item", target: step.target, key: step.key, destroyed: !step.destroyed };
    case "burn_set":
      // Restore the captured prior mark; its own inverse re-sets the new value.
      return { op: "burn_set", target: step.target, burn: step.prev, prev: step.burn };
    case "status_add":
      // Carry the added instance's facets onto the remove inverse so a redo
      // (invert-of-invert) rebuilds the exact instance (expiry + source + partner + cause).
      return {
        op: "status_remove",
        target: step.target,
        status: step.status,
        ...statusFacetsFromStep(step),
      };
    case "status_remove":
      // Same carry-through, symmetric direction.
      return {
        op: "status_add",
        target: step.target,
        status: step.status,
        ...statusFacetsFromStep(step),
      };
    case "status_suspend_set":
      return { op: "status_suspend_set", target: step.target, status: step.status, suspended: step.prev, prev: step.suspended };
    case "resource_set":
      // Restore the captured prior value; its own inverse re-sets the new value.
      // `prevAbsent` (Loading): the key genuinely didn't
      // exist before this step, so its undo must DELETE it (true absence), not leave
      // an explicit residue value that merely reads the same under a `?? default`.
      return step.prevAbsent
        ? { op: "resource_clear", target: step.target, key: step.key, restoreVal: step.val }
        : { op: "resource_set", target: step.target, key: step.key, val: step.prev, prev: step.val };
    case "resource_clear":
      // Reached either inverting a resource_set inverse (redo path) OR undoing a directly-emitted
      // `reload.ts` Reload step — either way, recreate the set that was cleared, flagged
      // `prevAbsent` again so a further undo re-clears.
      return { op: "resource_set", target: step.target, key: step.key, val: step.restoreVal, prev: 0, prevAbsent: true };
    case "resource_delta":
      return { op: "resource_delta", target: step.target, key: step.key, delta: -step.delta };
    case "move":
      // Swap from/to to walk the unit back to where it started.
      return { op: "move", target: step.target, from: step.to, to: step.from };
    case "forced_move":
      // Swap from/to to walk the displaced unit back; the distance is preserved as
      // the recorded magnitude (its inverse "pushes back" the same number of spaces).
      return { op: "forced_move", target: step.target, from: step.to, to: step.from, distance: step.distance, ...(step.teleport ? { teleport: true as const } : {}) };
    case "move_budget_delta":
      return { op: "move_budget_delta", target: step.target, delta: -step.delta };
    case "mine_armed_set":
      return { op: "mine_armed_set", target: step.target, armed: step.prev, prev: step.armed };
    case "concealed_set":
      return { op: "concealed_set", target: step.target, concealed: step.prev, prev: step.concealed };
    case "disclosure_set":
      return { op: "disclosure_set", target: step.target, disclosure: step.prev, prev: step.disclosure };
    case "features_revealed":
      return { op: "features_revealed", target: step.target, ids: step.prev, prev: step.ids };
    case "spawn_object":
      return { op: "despawn_object", combatant: step.combatant };
    case "despawn_object":
      return { op: "spawn_object", combatant: step.combatant };
    case "area_add":
      return { op: "area_remove", area: step.area };
    case "area_remove":
      return { op: "area_add", area: step.area };
    case "to_hit":
    case "damage":
    case "save":
    case "table_check":
    case "contest":
    case "miss_chance":
    case "cost_waived":
    case "ai_cascade_check":
    case "link":
    case "intercept":
      // Record-only: nothing to undo.
      return null;
  }
}

/**
 * Build the inverse **step list** for a resolved step list: the grounded steps'
 * inverses, in **reversed** order (undo the last mutation first), record-only
 * steps dropped. This is the resolved-step analogue of the reducer's
 * reversed-child-inverse batch composition — undo of a resolution folds these.
 */
export function invertSteps(steps: readonly ResolvedStep[]): ResolvedStep[] {
  const out: ResolvedStep[] = [];
  for (let i = steps.length - 1; i >= 0; i--) {
    const inv = invertStep(steps[i]!);
    if (inv) out.push(inv);
  }
  return out;
}
